import type { ParsedCSVRow } from "#lib/csv-parser"
import { parseDate, parseNumber } from "#lib/csv-parser"
import {
  getImportSession,
  saveImportSession,
  updateImportProgress,
  type ImportConfig,
  type ImportMapping,
  type ImportProgress,
  type ImportSession,
} from "#lib/indexeddb-import-store"
import type { ExpenseCategory } from "@home-finance-manager/shared/types/expense_categories"
import type { CreateExpense } from "@home-finance-manager/shared/types/expenses"
import { format } from "date-fns"
import * as React from "react"
import { useCategories, useCreateCategory } from "./use-categories"
import { useBulkCreateExpenses } from "./use-expenses"

const BATCH_SIZE = 50

export function useBulkExpenseImport(accountId: number) {
  const bulkCreate = useBulkCreateExpenses()
  const createCategory = useCreateCategory()
  const { data: allCategories = [], refetch: refetchCategories } = useCategories()
  const [isImporting, setIsImporting] = React.useState(false)
  const [currentSessionId, setCurrentSessionId] = React.useState<string | null>(null)
  const [progress, setProgress] = React.useState<ImportProgress | null>(null)
  const abortControllerRef = React.useRef<AbortController | null>(null)

  /**
   * Transform a CSV row to CreateExpense format
   */
  const transformRow = React.useCallback(
    (
      row: ParsedCSVRow,
      mapping: ImportMapping,
      config: ImportConfig,
      categoryMappings: Map<string, number>
    ): CreateExpense | null => {
      try {
        // Extract name
        const nameColumns = Array.isArray(mapping.name) ? mapping.name : [mapping.name || ""]
        const name = nameColumns
          .map((col) => row[col] || "")
          .filter(Boolean)
          .join(mapping.nameSeparator || " ")
          .trim()

        if (!name) return null

        // Extract description
        const descriptionColumns = Array.isArray(mapping.description)
          ? mapping.description
          : mapping.description
            ? [mapping.description]
            : []
        const description = descriptionColumns
          .map((col) => row[col] || "")
          .filter(Boolean)
          .join(mapping.descriptionSeparator || " ")
          .trim()

        // Extract amount
        const amountStr = row[mapping.amount || ""] || ""
        const parsedAmount = parseNumber(amountStr, config.decimalSeparator)
        // Convert negative amounts to positive
        const amount = parsedAmount ? Math.abs(parsedAmount) : null
        if (!amount || amount <= 0) return null

        // Extract date
        const dateColumn = Array.isArray(mapping.date) ? mapping.date[0] : mapping.date || ""
        const dateStr = row[dateColumn] || ""
        const date = parseDate(dateStr, config.dateFormat)
        if (!date) return null

        // Extract category
        const categoryColumn = Array.isArray(mapping.category)
          ? mapping.category[0]
          : mapping.category
        const categoryName = categoryColumn ? row[categoryColumn]?.trim() : undefined
        const categoryId = categoryName
          ? categoryMappings.get(categoryName) || mapping.categoryMappings?.[categoryName]
          : undefined

        if (!categoryId || (typeof categoryId === "string" && categoryId === "new")) {
          return null
        }

        const expense: CreateExpense = {
          name,
          description: description || undefined,
          amount,
          recurring: "one-time",
          isVirtual: false,
          nextPaymentDate: format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
          expenseCategoryId: typeof categoryId === "number" ? categoryId : 0,
        }

        return expense
      } catch (error) {
        console.error("Error transforming row:", error)
        return null
      }
    },
    []
  )

  /**
   * Start the import process
   */
  const startImport = React.useCallback(
    async (
      rows: ParsedCSVRow[],
      mapping: ImportMapping,
      config: ImportConfig,
      selectedRows: boolean[],
      categories: ExpenseCategory[]
    ) => {
      setIsImporting(true)
      abortControllerRef.current = new AbortController()

      // Create category mappings
      const categoryMappings = new Map<string, number>()
      const categoriesToCreate = new Map<string, string>()

      // Process category mappings
      if (mapping.categoryMappings) {
        for (const [categoryName, categoryIdOrNew] of Object.entries(mapping.categoryMappings)) {
          if (categoryIdOrNew === "new") {
            categoriesToCreate.set(categoryName, categoryName)
          } else {
            categoryMappings.set(categoryName, categoryIdOrNew)
          }
        }
      }

      // Create new categories (or use existing ones if they already exist)
      // Merge passed categories with allCategories to ensure we have the latest list
      const mergedCategories = [
        ...categories,
        ...allCategories.filter((cat) => !categories.some((c) => c.id === cat.id)),
      ]

      // Track category creation errors
      const categoryErrors: Array<{ index: number; error: string; row: any }> = []

      for (const [categoryName, categoryNameValue] of categoriesToCreate.entries()) {
        try {
          // First check if a category with this name already exists (case-insensitive)
          let existingCategory = mergedCategories.find(
            (cat) => cat.name.toLowerCase() === categoryNameValue.toLowerCase()
          )

          if (existingCategory) {
            // Use the existing category
            categoryMappings.set(categoryName, existingCategory.id)
          } else {
            // Try to create a new category
            try {
              const newCategory = await createCategory.mutateAsync({
                name: categoryNameValue,
                description: "", // Explicitly set to empty string, not null
                // Don't specify color - let the service generate a unique one
              })
              categoryMappings.set(categoryName, newCategory.id)
              // Update categories list for subsequent lookups
              categories.push(newCategory)
              mergedCategories.push(newCategory)
            } catch (createError: any) {
              // If creation fails (e.g., duplicate), refetch categories and try to find it
              const errorMessage = createError?.message || ""
              const errorResponse = createError?.response
              let errorDetails = errorMessage

              // Try to extract more detailed error from response
              if (errorResponse) {
                try {
                  const errorData = await errorResponse.json().catch(() => ({}))
                  if (errorData.errors && Array.isArray(errorData.errors)) {
                    errorDetails = errorData.errors.join(", ")
                  } else if (errorData.properties) {
                    const propErrors: string[] = []
                    for (const [prop, value] of Object.entries(errorData.properties)) {
                      if (value && typeof value === "object" && "errors" in value) {
                        propErrors.push(`${prop}: ${(value as any).errors.join(", ")}`)
                      }
                    }
                    if (propErrors.length > 0) {
                      errorDetails = propErrors.join("; ")
                    }
                  }
                } catch {
                  // If parsing fails, use the message
                }
              }

              if (
                errorMessage.includes("already exists") ||
                errorMessage.includes("duplicate") ||
                errorMessage.includes("Conflict") ||
                errorResponse?.status === 400 ||
                errorResponse?.status === 409
              ) {
                // Category already exists, refetch and try to find it
                console.warn(`Category "${categoryNameValue}" already exists, looking it up...`)
                try {
                  const { data: refreshedCategories } = await refetchCategories()
                  const foundCategory = refreshedCategories?.find(
                    (cat) => cat.name.toLowerCase() === categoryNameValue.toLowerCase()
                  )
                  if (foundCategory) {
                    categoryMappings.set(categoryName, foundCategory.id)
                    categories.push(foundCategory)
                    mergedCategories.push(foundCategory)
                  } else {
                    // Add error if category not found after refetch
                    categoryErrors.push({
                      index: -1, // Use -1 to indicate category error
                      error: `Failed to create category "${categoryNameValue}": ${errorDetails}`,
                      row: { categoryName: categoryNameValue },
                    })
                    console.error(
                      `Category "${categoryNameValue}" exists but could not be found after refetch`
                    )
                  }
                } catch (refetchError) {
                  categoryErrors.push({
                    index: -1,
                    error: `Failed to create category "${categoryNameValue}": ${errorDetails}`,
                    row: { categoryName: categoryNameValue },
                  })
                  console.error(`Failed to refetch categories after duplicate error:`, refetchError)
                }
              } else {
                // Add error for other failures
                categoryErrors.push({
                  index: -1,
                  error: `Failed to create category "${categoryNameValue}": ${errorDetails}`,
                  row: { categoryName: categoryNameValue },
                })
                console.error(`Failed to create category ${categoryName}:`, createError)
              }
            }
          }
        } catch (error: any) {
          categoryErrors.push({
            index: -1,
            error: `Failed to process category "${categoryName}": ${error?.message || "Unknown error"}`,
            row: { categoryName },
          })
          console.error(`Failed to process category ${categoryName}:`, error)
        }
      }

      // Filter rows based on selection
      const filteredRows = rows.filter((_, index) => selectedRows[index])

      // Transform rows to expenses
      const expenses: (CreateExpense | null)[] = filteredRows.map((row) =>
        transformRow(row, mapping, config, categoryMappings)
      )

      // Filter out null expenses
      const validExpenses = expenses.filter((exp): exp is CreateExpense => exp !== null)

      // Create import session
      const sessionId = `import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      const initialProgress: ImportProgress = {
        id: sessionId,
        accountId,
        totalRows: validExpenses.length,
        processedRows: 0,
        successfulRows: 0,
        failedRows: 0,
        errors: categoryErrors, // Include category creation errors
        status: "in_progress",
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }

      const session: ImportSession = {
        id: sessionId,
        accountId,
        parsedRows: filteredRows,
        mapping,
        config,
        progress: initialProgress,
      }

      await saveImportSession(session)
      setCurrentSessionId(sessionId)
      setProgress(initialProgress)

      // Process in batches
      await processBatches(sessionId, validExpenses, abortControllerRef.current.signal)
    },
    [accountId, transformRow, createCategory]
  )

  /**
   * Process expenses in batches
   */
  const processBatches = React.useCallback(
    async (sessionId: string, expenses: CreateExpense[], signal: AbortSignal) => {
      let processedCount = 0
      let successfulCount = 0
      let failedCount = 0
      const errors: Array<{ index: number; error: string; row: any }> = []

      for (let i = 0; i < expenses.length; i += BATCH_SIZE) {
        if (signal.aborted) {
          await updateImportProgress(sessionId, { status: "paused" })
          setProgress((prev) => (prev ? { ...prev, status: "paused" } : null))
          setIsImporting(false)
          return
        }

        const batch = expenses.slice(i, i + BATCH_SIZE)

        try {
          const result = await bulkCreate.mutateAsync(batch)

          processedCount += batch.length
          successfulCount += result.success
          failedCount += result.failed

          // Add errors from batch result
          for (const error of result.errors) {
            errors.push({
              index: i + error.index,
              error: error.error,
              row: batch[error.index],
            })
          }

          // Update progress
          const updatedProgress: Partial<ImportProgress> = {
            processedRows: processedCount,
            successfulRows: successfulCount,
            failedRows: failedCount,
            errors,
            status: processedCount >= expenses.length ? "completed" : "in_progress",
          }

          await updateImportProgress(sessionId, updatedProgress)
          setProgress((prev) =>
            prev
              ? {
                  ...prev,
                  ...updatedProgress,
                }
              : null
          )

          // Use requestIdleCallback or setTimeout to avoid blocking
          await new Promise((resolve) => {
            if ("requestIdleCallback" in window) {
              requestIdleCallback(() => resolve(undefined), { timeout: 100 })
            } else {
              setTimeout(resolve, 0)
            }
          })
        } catch (error: any) {
          // Batch failed, mark all as failed
          failedCount += batch.length
          for (let j = 0; j < batch.length; j++) {
            errors.push({
              index: i + j,
              error: error.message || "Failed to import expense",
              row: batch[j],
            })
          }

          const updatedProgress: Partial<ImportProgress> = {
            processedRows: processedCount + batch.length,
            successfulRows: successfulCount,
            failedRows: failedCount,
            errors,
          }

          await updateImportProgress(sessionId, updatedProgress)
          setProgress((prev) =>
            prev
              ? {
                  ...prev,
                  ...updatedProgress,
                }
              : null
          )
        }
      }

      setIsImporting(false)
      if (processedCount >= expenses.length) {
        await updateImportProgress(sessionId, { status: "completed" })
        setProgress((prev) => (prev ? { ...prev, status: "completed" } : null))
      }
    },
    [bulkCreate]
  )

  /**
   * Resume a paused import
   */
  const resumeImport = React.useCallback(
    async (sessionId: string) => {
      const session = await getImportSession(sessionId)
      if (!session) {
        throw new Error("Import session not found")
      }

      setIsImporting(true)
      abortControllerRef.current = new AbortController()

      // Reconstruct expenses from session
      const categoryMappings = new Map<string, number>()
      if (session.mapping.categoryMappings) {
        for (const [name, id] of Object.entries(session.mapping.categoryMappings)) {
          if (typeof id === "number") {
            categoryMappings.set(name, id)
          }
        }
      }

      const expenses: (CreateExpense | null)[] = session.parsedRows.map((row) =>
        transformRow(row, session.mapping, session.config, categoryMappings)
      )

      const validExpenses = expenses.filter((exp): exp is CreateExpense => exp !== null)

      // Continue from where we left off
      const remainingExpenses = validExpenses.slice(session.progress.processedRows)

      await updateImportProgress(sessionId, { status: "in_progress" })
      setProgress(session.progress)
      setCurrentSessionId(sessionId)

      await processBatches(sessionId, remainingExpenses, abortControllerRef.current.signal)
    },
    [transformRow, processBatches]
  )

  /**
   * Pause the current import
   */
  const pauseImport = React.useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    if (currentSessionId) {
      await updateImportProgress(currentSessionId, { status: "paused" })
      setProgress((prev) => (prev ? { ...prev, status: "paused" } : null))
    }
    setIsImporting(false)
  }, [currentSessionId])

  /**
   * Cancel the current import
   */
  const cancelImport = React.useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    if (currentSessionId) {
      await updateImportProgress(currentSessionId, { status: "failed" })
      setProgress((prev) => (prev ? { ...prev, status: "failed" } : null))
    }
    setIsImporting(false)
    setCurrentSessionId(null)
  }, [currentSessionId])

  return {
    isImporting,
    progress,
    startImport,
    resumeImport,
    pauseImport,
    cancelImport,
  }
}
